import type { RequestStatus } from "./dto";

/**
 * The capacity model as data: what an estimate may be, how many days a week
 * a person has by default, and which statuses count against someone's time.
 * The Worker (validation) and the SPA (the estimate select, the Capacity
 * page) both read from here, so the scale or the default moves in one place.
 */

/** The estimation scale, in days of effort. The same five values the PM set
 *  for ClickUp (1 point = 1 day); anything bigger than 5 is split into
 *  separate requests rather than sized. An allowlist, not bounds: 4 is as
 *  wrong as 4.3. */
export const ESTIMATE_SCALE = [0.5, 1, 2, 3, 5] as const;

/** True for a value on the estimation scale. */
export function isEstimate(value: unknown): value is number {
  return typeof value === "number" && (ESTIMATE_SCALE as readonly number[]).includes(value);
}

/** "half a day", "1 day", "2 days" — how an estimate reads in prose. */
export function describeDays(days: number): string {
  if (days === 0.5) return "half a day";
  return `${days} ${days === 1 ? "day" : "days"}`;
}

/** Focus-days a week for anyone with no override: five minus the meetings,
 *  support and ad-hoc work that never shows up as a request. */
export const DEFAULT_CAPACITY_DAYS_PER_WEEK = 4;

/** A person's weekly capacity: their override, or the default. The upper
 *  bound is seven, not five — arguing with someone modelling a crunch week
 *  through a 422 is not worth the code. */
export function capacityFor(member: { capacityDaysPerWeek: number | null }): number {
  return member.capacityDaysPerWeek ?? DEFAULT_CAPACITY_DAYS_PER_WEEK;
}

/** True for a weekly capacity we accept: more than zero, at most seven, in halves. */
export function isCapacity(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 && value <= 7 && Number.isInteger(value * 2);
}

/**
 * How a status counts against someone's time.
 *
 *  - active     — being worked on now; charged
 *  - queued     — ours to do, in order; charged
 *  - verifying  — the work is written and the customer is checking it; the
 *                 days were spent, not committed, so charging the estimate
 *                 again would double-count
 *  - parked     — blocked on the customer or handed to Core Eng; cannot be
 *                 scheduled, so not charged, but still shown
 *  - closed     — nothing to do
 */
export type CapacityClass = "active" | "queued" | "verifying" | "parked" | "closed";

/** Exhaustive on purpose: a twelfth status is a type error here until
 *  someone decides how it counts, rather than a silent zero. */
export const CAPACITY_CLASS: Record<RequestStatus, CapacityClass> = {
  new: "queued",
  backlog: "queued",
  decision: "queued",
  triaged: "queued",
  progress: "active",
  waiting: "parked",
  validation: "verifying",
  done: "closed",
  supported: "closed",
  wontdo: "closed",
  escalated: "parked",
};
